// src/pages/exercise/components/Stores/exerciseStore.js
import { create } from "zustand";
import { exercisePool } from "../../data/exercisePool";
import { CHARACTER_CONFIG } from "../../data/characterConfig";

const REROLLS_KEY  = "esp_rerolls";
const PROGRESS_KEY = "esp_session_progress";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function pickMissions(charIndex) {
  const config = CHARACTER_CONFIG[charIndex];
  const pool = exercisePool.filter((ex) =>
    ex.moodMatch.includes(config.moodToken)
  );
  return [...pool].sort(() => Math.random() - 0.5).slice(0, 3);
}

function loadProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveProgress(progress) {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch { /* ignore */ }
}

const defaultProgress = {
  date: todayISO(),
  completedIds: [],
  totalCompleted: 0,
};

export const useStore = create((set, get) => ({
  // ── EXISTING STATE ────────────────────────────────────────────────────────
  phase: "SELECTION",
  charIndex: 1,
  scrollLocked: false,
  lastTransitionTime: 0,
  selectedExercise: null,
  suggestionFilter: "all",

  // ── MISSION STATE ─────────────────────────────────────────────────────────
  missionPool: [],
  completedMissions: [],
  rerollsLeft: 2,
  rerollsDate: null,

  // ── TIMER STATE ───────────────────────────────────────────────────────────
  activeTimer: null,

  // ── PROGRESS STATE ────────────────────────────────────────────────────────
  sessionProgress: defaultProgress,

  // ── EXISTING ACTIONS ──────────────────────────────────────────────────────
  nextChar: () =>
    set((state) => {
      if (state.phase !== "SELECTION") return {};
      const next = state.charIndex + 1;
      return { charIndex: next > 4 ? 1 : next };
    }),

  prevChar: () =>
    set((state) => {
      if (state.phase !== "SELECTION") return {};
      const prev = state.charIndex - 1;
      return { charIndex: prev < 1 ? 4 : prev };
    }),

  triggerTransition1: () => {
    const state = get();
    const now = Date.now();
    if (state.phase !== "SELECTION") return;
    if (state.scrollLocked) return;
    if (now - state.lastTransitionTime < 2000) return;
    set({ phase: "TRANSITION1", scrollLocked: true, lastTransitionTime: now });
  },

  triggerTransition2: () => {
    const state = get();
    const now = Date.now();
    if (state.phase !== "EXERCISE") return;
    if (state.scrollLocked) return;
    if (now - state.lastTransitionTime < 2000) return;
    set({ phase: "TRANSITION2", scrollLocked: true, lastTransitionTime: now });
  },

  completeTransition1: () => {
    const state = get();
    if (state.phase !== "TRANSITION1") return;
    
    // ✅ Auto-seed missions for the panel — no external initMissions call needed
    const missions = pickMissions(state.charIndex);
    
    set({ 
      phase: "EXERCISE", 
      scrollLocked: false,
      missionPool: missions,
      completedMissions: []
    });
  },

  forceExercise: () => {
    const state = get();
    if (state.phase === "TRANSITION1" || state.phase === "SELECTION") {
      console.warn("Store: forceExercise called — forcing phase to EXERCISE");
      set({ phase: "EXERCISE", scrollLocked: false });
    }
  },

  completeTransition2: () => {
    const state = get();
    if (state.phase !== "TRANSITION2") return;
    set({ phase: "SELECTION", scrollLocked: false });
  },

  completeTransitionImmediate: () => {
    const state = get();
    if (state.phase === "TRANSITION1") {
      set({ phase: "EXERCISE", scrollLocked: false });
    } else if (state.phase === "TRANSITION2") {
      set({ phase: "SELECTION", scrollLocked: false });
    }
  },

  resetExperience: () =>
    set({
      phase: "SELECTION",
      charIndex: 1,
      scrollLocked: false,
      lastTransitionTime: 0,
    }),

  setSelectedExercise: (exercise) => set({ selectedExercise: exercise }),
  clearSelectedExercise: () => set({ selectedExercise: null }),
  setSuggestionFilter: (filter) => set({ suggestionFilter: filter }),

  // ── MISSION ACTIONS ───────────────────────────────────────────────────────
  initMissions: (charIndex) => {
    set({ missionPool: pickMissions(charIndex), completedMissions: [] });
  },

  checkAndResetDailyRerolls: () => {
    const today = todayISO();

    // ── Rerolls ──
    try {
      const raw = localStorage.getItem(REROLLS_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (stored.date === today) {
          set({ rerollsLeft: stored.left, rerollsDate: stored.date });
        } else {
          throw new Error("stale");
        }
      } else {
        throw new Error("missing");
      }
    } catch {
      localStorage.setItem(REROLLS_KEY, JSON.stringify({ date: today, left: 2 }));
      set({ rerollsLeft: 2, rerollsDate: today });
    }

    // ── Progress ──
    const stored = loadProgress();
    if (stored && stored.date === today) {
      set({ sessionProgress: stored });
    } else {
      const fresh = {
        date: today,
        completedIds: [],
        totalCompleted: stored?.totalCompleted ?? 0,
      };
      saveProgress(fresh);
      set({ sessionProgress: fresh });
    }
  },

  rerollMission: (missionIndex, charIndex) => {
    const { rerollsLeft, missionPool, rerollsDate } = get();
    if (rerollsLeft <= 0) return;
    const config = CHARACTER_CONFIG[charIndex];
    const usedIds = new Set(missionPool.map((ex) => ex.id));
    const candidates = exercisePool.filter(
      (ex) => ex.moodMatch.includes(config.moodToken) && !usedIds.has(ex.id)
    );
    if (candidates.length === 0) return;
    const newExercise = candidates[Math.floor(Math.random() * candidates.length)];
    const newPool = [...missionPool];
    newPool[missionIndex] = newExercise;
    const newLeft = rerollsLeft - 1;
    localStorage.setItem(REROLLS_KEY, JSON.stringify({ date: rerollsDate ?? todayISO(), left: newLeft }));
    set({ missionPool: newPool, rerollsLeft: newLeft });
  },

  completeMission: (missionIndex) => {
    const { completedMissions, missionPool, sessionProgress } = get();
    if (completedMissions.includes(missionIndex)) return;

    const exercise = missionPool[missionIndex];
    const newCompleted = [...completedMissions, missionIndex];

    // Update session progress
    const alreadyRecorded = exercise && sessionProgress.completedIds.includes(exercise.id);
    const newProgress = alreadyRecorded
      ? sessionProgress
      : {
          ...sessionProgress,
          completedIds: [...sessionProgress.completedIds, exercise?.id].filter(Boolean),
          totalCompleted: sessionProgress.totalCompleted + 1,
        };

    saveProgress(newProgress);
    set({ completedMissions: newCompleted, sessionProgress: newProgress });
  },

  // ── TIMER ACTIONS ─────────────────────────────────────────────────────────
  startTimer: (exercise, missionIndex) =>
    set({ activeTimer: { exercise, missionIndex } }),

  stopTimer: () => set({ activeTimer: null }),
}));